/* eslint-disable @typescript-eslint/no-explicit-any */
import { refreshToken } from "@/services/rest/auth";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// SSR에서 백엔드로 전달할 쿠키 화이트리스트.
// BE가 실제로 읽는 인증 쿠키만 보낸다(auth_middleware: ATN/RTN, CMS 프록시: IDP_ATN).
// 전체 쿠키를 통째로 보내면 ALB 헤더 한도(약 8~16KB)를 넘겨 400이 난다.
const SSR_FORWARD_COOKIE_NAMES = [
  "DA_DOOH_ATN",
  "DA_DOOH_RTN",
  "DA_DOOH_IDP_ATN",
];

interface FetcherOptions<T = any> {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: T;
  queryParams?: Record<
    string,
    string | number | boolean | undefined | string[] | number[]
  >;
  signal?: AbortSignal;
  cache?: RequestCache;
  next?: NextFetchRequestConfig;
}

export async function fetcher<R = any, B = any>(
  url: string,
  options?: FetcherOptions<B>
): Promise<R> {
  const {
    method = "GET",
    headers = {},
    body,
    queryParams,
    signal,
    cache = "no-store",
    next,
  } = options || {};

  // Build query params
  const cleanParams = new URLSearchParams();
  Object.entries(queryParams || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const joined = value
        .filter((v) => v !== undefined && v !== null)
        .map(String)
        .join(",");
      if (joined) cleanParams.set(key, joined);
    } else if (value !== undefined && value !== null) {
      cleanParams.set(key, String(value));
    }
  });
  const queryString = method === "GET" ? cleanParams.toString() : "";

  const isServer = typeof window === "undefined";

  let fullUrl = url;
  if (queryString) {
    fullUrl += fullUrl.includes("?") ? `&${queryString}` : `?${queryString}`;
  }

  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const DEFAULT_LANG = "kr";

  async function getAcceptLanguage(isServer: boolean): Promise<string> {
    if (isServer) {
      const { cookies } = await import("next/headers");
      const cookieStore = await cookies();
      return cookieStore.get("NEXT_LOCALE")?.value ?? DEFAULT_LANG;
    }

    if (typeof document !== "undefined") {
      const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
      return match?.[1] ?? DEFAULT_LANG;
    }

    return DEFAULT_LANG;
  }
  const acceptLanguage = await getAcceptLanguage(isServer);
  const fetchOptions: RequestInit = {
    method,
    ...(isServer ? {} : { credentials: "include" }),
    headers: {
      ...(!isFormData && { "Content-Type": "application/json" }),
      "Accept-Language": acceptLanguage,
      ...headers,
    },
    cache,
    ...(signal && { signal }),
    ...(next && { next }),
  };

  if (body && method !== "GET") {
    fetchOptions.body = isFormData ? (body as any) : JSON.stringify(body);
  }

  // Copy cookies on server (SSR)
  if (isServer) {
    const { cookies } = await import("next/headers");
    const cookieStore = await cookies();
    // ALB는 Cookie 헤더가 약 8~16KB를 넘으면 앱(uvicorn)에 닿기 전에 400(빈 본문)을
    // 반환한다. 로그인 시 누적된 전체 쿠키를 통째로 전달하면 이 한도를 넘어, 비회원은
    // 되는데 로그인 사용자만 상세 SSR 요청이 400 나는 증상이 발생한다.
    // → BE(auth_middleware / CMS 프록시)가 실제로 읽는 인증 쿠키만 전달한다.
    const cookieString = cookieStore
      .getAll()
      .filter((c) => SSR_FORWARD_COOKIE_NAMES.includes(c.name))
      .map((c) => `${c.name}=${c.value}`)
      .join("; ");
    if (cookieString) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Cookie: cookieString,
      };
    }
  }

  const isDebug = process.env.NODE_ENV !== "production";

  async function doFetch(isRetry = false): Promise<R> {
    const start = Date.now();
    try {
      const res = await fetch(fullUrl, fetchOptions);
      const duration = Date.now() - start;
      const contentType = res.headers.get("content-type");
      const isJson = contentType?.includes("application/json");

      if (res.status === 401) {
        if (isDebug)
          console.warn(`[fetcher] 401 → ${method} ${fullUrl} (${duration}ms)`);

        if (isRetry) {
          if (typeof window !== "undefined") {
            window.location.href = "/";
            throw { status: 401, message: "Unauthorized" };
          } else {
            throw { status: 401, message: "Unauthorized" };
          }
        }

        try {
          const refreshRes = await refreshToken();
          if ((refreshRes as any)?.status === 401) {
            if (typeof window !== "undefined") {
              window.location.href = "/";
              throw { status: 401, message: "Unauthorized" };
            } else {
              throw { status: 401, message: "Unauthorized" };
            }
          }

          return await doFetch(true);
        } catch (refreshErr: any) {
          if (isDebug && refreshErr?.status !== 401)
            console.error(`[fetcher] refreshToken error:`, refreshErr);
          if (typeof window !== "undefined") {
            window.location.href = "/";
            throw { status: 401, message: "Unauthorized" };
          } else {
            throw { status: 401, message: "Unauthorized" };
          }
        }
      }

      if (!res.ok) {
        const errBody = isJson ? await res.json() : await res.text();
        console.error(
          `[fetcher] ${method} ${fullUrl} → ${res.status} (${duration}ms)`,
          isDebug ? errBody : ""
        );

        // status 필드 보장 + BE 메시지 전달
        // throw new Error() 대신 payload 객체 throw: 에러 바디에 status가 정확히 담김
        const errorPayload =
          typeof errBody === "string"
            ? { status: res.status, message: errBody }
            : { status: res.status, ...errBody };

        throw errorPayload;
      }

      if (isJson) {
        const json = await res.json();
        return json;
      } else {
        return (await res.text()) as any;
      }
    } catch (err: any) {
      const duration = Date.now() - start;
      if (
        err?.digest !== "NEXT_REDIRECT" &&
        err?.digest !== "NEXT_NOT_FOUND" &&
        err?.status !== 401
      ) {
        console.error(
          `[fetcher] Exception in ${method} ${fullUrl} (${duration}ms):`,
          err?.message ?? err
        );
      }
      throw err;
    }
  }

  try {
    return await doFetch();
  } catch (err: any) {
    if (err.digest === "NEXT_NOT_FOUND" || err.digest === "NEXT_REDIRECT") {
      throw err;
    }
    throw {
      status: err?.status ?? 500,
      message: err?.message ?? "Unknown error",
      data: err?.data ?? null,
      message_code: err?.message_code ?? "",
    };
  }
}
